import { useCallback, useRef } from 'react';
import { saveTourRoute, updateTourRoute } from 'features/Responder/features/Tours/services/tourApi.js';
import normalizeTravelMode from 'features/Responder/features/Tours/features/TourBuilder/utils/normalizeTravelMode';

/**
 * Persist routes to backend with common validation.
 * @param {object} params
 * @param {boolean} params.isLooped
 */
export function useRoutePersistence({
  canSaveRoute,
  editingTourId,
  routeWaypointOrder,
  checkpointMetaById,
  stopMetaById,
  routeData,
  builderTourData,
  activeTourData,
  addTour,
  resetBuilder,
  setSaveError,
  setSaveSuccess,
  setSaving,
  onPersistSuccess,
  isLooped = false,
}) {
  const isSavingRef = useRef(false);
  const handleSaveRoute = useCallback(
    async (mode = 'new') => {
      if (isSavingRef.current) return;
      const SAVE_TIMEOUT_MS = 20000;
      let didComplete = false;
      let timeoutId;
      setSaveError('');
      setSaveSuccess('');
      if (!canSaveRoute) {
        setSaveError('Build a route with a valid start and end before saving.');
        return;
      }
      if (mode === 'update' && !editingTourId) {
        setSaveError('Select a tour to update first.');
        return;
      }
      isSavingRef.current = true;
      setSaving(true);
      timeoutId = setTimeout(() => {
        if (didComplete) return;
        setSaveError('Save is taking longer than expected. Please try again.');
        setSaving(false);
        isSavingRef.current = false;
      }, SAVE_TIMEOUT_MS);
      try {
        const toTravelNode = (node, idx, type, legSeconds = 0, totalSeconds = 0) => {
          const geometry =
            node.geometry ||
            (node.geometryType || node.shapeMode
              ? { type: node.geometryType || node.shapeMode }
              : null);
          return {
            id: node.id || `node-${idx}`,
            name: node.displayName || node.label || `Checkpoint ${idx + 1}`,
            coords: [Number(node.lat), Number(node.lng)],
            type,
            mode: normalizeTravelMode(node.mode || node.travelMode),
            address: node.address || '',
            radius: Number(node.radiusMeters || 100),
            geometry,
            waitTime:
              type === 'origin'
                ? null
                : (() => {
                    const val = Number(node.stopTimeMinutes);
                    return Number.isFinite(val) && val > 0 ? val : null;
                  })(),
            segmentTime:
              type === 'origin'
                ? null
                : (() => {
                    const minutes = Math.max(0, Math.round((legSeconds || 0) / 60));
                    return minutes > 0 ? minutes : null;
                  })(),
            bonusTime:
              type === 'origin'
                ? null
                : (() => {
                    const seconds =
                      node.timeWindowSeconds ||
                      (Number.isFinite(Number(node.timeWindowMinutes))
                        ? Number(node.timeWindowMinutes) * 60
                        : 0);
                    const minutes = Math.max(0, Math.round(seconds / 60));
                    return minutes > 0 ? minutes : null;
                  })(),
            totalTimeFromStart:
              type === 'origin'
                ? null
                : (() => {
                    const minutes = Math.max(0, Math.round((totalSeconds || 0) / 60));
                    return minutes > 0 ? minutes : null;
                  })(),
          };
        };

        const parseDurationSeconds = (val) => {
          if (typeof val === 'number') return val;
          if (typeof val === 'string') {
            const n = parseFloat(val.replace('s', ''));
            return Number.isFinite(n) ? n : 0;
          }
          return 0;
        };

        const nodes = [];
        routeWaypointOrder.forEach((item, idx) => {
          if (item.type === 'checkpoint') {
            const meta = checkpointMetaById[item.id] || {};
            nodes.push(
              toTravelNode(
                {
                  ...item.ref,
                  lat: item.ref?.lat,
                  lng: item.ref?.lng,
                  address: item.ref?.address,
                  radiusMeters: item.ref?.radiusMeters,
                  timeWindowSeconds: item.ref?.timeWindowSeconds,
                  geometry: item.ref?.geometry,
                },
                nodes.length,
                'checkpoint',
                meta.legSeconds,
                meta.tripTotalSeconds,
              ),
            );
          } else {
            const meta = stopMetaById[item.id] || {};
            const isLastInOrder = idx === routeWaypointOrder.length - 1;
            const isDestination = isLastInOrder && !isLooped;
            const nodeType =
              item.type === 'origin' ? 'origin' : isDestination ? 'destination' : 'checkpoint';
            nodes.push(
              toTravelNode(
                {
                  ...item.ref,
                  lat: item.ref?.lat,
                  lng: item.ref?.lng,
                  address: item.ref?.address,
                  stopTimeMinutes: item.ref?.stopTimeMinutes,
                  timeWindowMinutes: item.ref?.timeWindowMinutes,
                  geometry: item.ref?.geometry,
                },
                nodes.length,
                nodeType,
                meta.legSeconds,
                meta.tripTotalSeconds,
              ),
            );
          }
        });

        const legs = Array.isArray(routeData?.legs) ? routeData.legs : [];
        if (isLooped && routeWaypointOrder.length) {
          const originItem =
            routeWaypointOrder.find((item) => item.type === 'origin') ||
            routeWaypointOrder.find((item) => item.type === 'stop');
          if (originItem?.ref?.lat != null && originItem?.ref?.lng != null && legs.length) {
            const loopLegSeconds = parseDurationSeconds(legs[legs.length - 1]?.duration);
            const totalLegSeconds = legs.reduce(
              (sum, leg) => sum + parseDurationSeconds(leg?.duration),
              0,
            );
            const loopRef = {
              ...originItem.ref,
              label:
                originItem.ref?.label ||
                originItem.ref?.address ||
                originItem.ref?.displayName ||
                'Start',
              mode: normalizeTravelMode(originItem.ref?.returnMode || originItem.ref?.mode),
              stopTimeMinutes: 0,
              timeWindowMinutes: 0,
              timeWindowSeconds: 0,
            };
            nodes.push(
              toTravelNode(loopRef, nodes.length, 'destination', loopLegSeconds, totalLegSeconds),
            );
          }
        }
        const nodesWithLegs = nodes.map((node, idx) => {
          const legIdx = idx - 1;
          if (legIdx < 0) return { ...node };
          const rawLeg = legs[legIdx] || {};
          const {
            mode: legMode,
            travelMode: legTravelMode,
            duration: legDuration,
            distanceMeters,
            polyline,
            encodedPolyline,
            ...restLeg
          } = rawLeg;
          return {
            ...node,
            leg: {
              distanceMeters: Number.isFinite(distanceMeters) ? distanceMeters : null,
              durationSeconds: parseDurationSeconds(legDuration),
              encodedPolyline: encodedPolyline || polyline || null,
              travelMode:
                (legTravelMode || legMode || 'DRIVING').toUpperCase() === 'WALKING'
                  ? 'WALKING'
                  : 'DRIVING',
              ...restLeg,
            },
          };
        });

        const payload = {
          name: builderTourData?.name || activeTourData?.name || 'Draft tour',
          nodes: nodesWithLegs,
          encodedPolyline: routeData?.polyline || ''.toString(),
          routeOptimization: 'fastest',
        };
        const result =
          mode === 'update'
            ? await updateTourRoute(editingTourId, payload)
            : await saveTourRoute(payload);
        const rawSavedTour = result?.tour || result?.trip || result;
        const savedTour =
          rawSavedTour && typeof rawSavedTour === 'object'
            ? {
                ...rawSavedTour,
                name: rawSavedTour?.name || payload?.name || 'Draft tour',
                nodes:
                  Array.isArray(rawSavedTour?.nodes) && rawSavedTour.nodes.length
                    ? rawSavedTour.nodes
                    : nodesWithLegs,
                legs:
                  Array.isArray(rawSavedTour?.legs) && rawSavedTour.legs.length
                    ? rawSavedTour.legs
                    : Array.isArray(routeData?.legs)
                      ? routeData.legs
                      : [],
                encodedPolyline:
                  rawSavedTour?.encodedPolyline ||
                  rawSavedTour?.polyline ||
                  payload?.encodedPolyline ||
                  '',
                totalDuration: rawSavedTour?.totalDuration ?? routeData?.totalDuration ?? null,
                totalDistanceMeters:
                  rawSavedTour?.totalDistanceMeters ?? routeData?.totalDistanceMeters ?? null,
                isLooped:
                  typeof rawSavedTour?.isLooped === 'boolean' ? rawSavedTour.isLooped : isLooped,
              }
            : null;
        if (savedTour?.id != null && typeof addTour === 'function') {
          // Keep TourContext list in sync immediately; addTour is an upsert by id.
          addTour(savedTour);
        }
        setSaveError('');
        setSaveSuccess(
          mode === 'update' ? 'tour updated successfully.' : 'tour saved successfully.',
        );

        resetBuilder();
        if (typeof onPersistSuccess === 'function') {
          onPersistSuccess({ mode, savedTour });
        }
      } catch (err) {
        setSaveError(err.message || 'Failed to save tour');
      } finally {
        didComplete = true;
        if (timeoutId) clearTimeout(timeoutId);
        setSaving(false);
        isSavingRef.current = false;
      }
    },
    [
      activeTourData?.name,
      builderTourData?.name,
      canSaveRoute,
      checkpointMetaById,
      editingTourId,
      routeData?.legs,
      routeData?.polyline,
      routeWaypointOrder,
      stopMetaById,
      resetBuilder,
      setSaveError,
      setSaveSuccess,
      setSaving,
      addTour,
      onPersistSuccess,
      isLooped,
    ],
  );

  return { handleSaveRoute };
}
